import { useQuery } from "@tanstack/react-query";
import { usageApi } from "@/api/usage";
import { useSession } from "@/stores/session";
import { useSidebar } from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/cn";

const PLAN_LABEL: Record<string, string> = { free: "Free", pro: "Pro", scale: "Scale" };

/** Compact step count: 950 → "950", 12400 → "12.4K", 1_000_000 → "1M". */
function compact(n: number): string {
  if (n >= 1_000_000) return trim(n / 1_000_000) + "M";
  if (n >= 1_000) return trim(n / 1_000) + "K";
  return String(n);
}
function trim(n: number): string {
  return n.toFixed(n % 1 === 0 ? 0 : 1).replace(/\.0$/, "");
}

function Ring({
  frac,
  over,
  unlimited,
  size,
  stroke,
}: {
  frac: number;
  over: boolean;
  unlimited: boolean;
  size: number;
  stroke: number;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.min(1, Math.max(0, frac));
  const off = c * (1 - clamped);
  const pct = unlimited ? "∞" : `${Math.round(clamped * 100)}%`;
  return (
    <span className="relative inline-flex shrink-0 items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-border" />
        {!unlimited && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={off}
            className={cn("transition-[stroke-dashoffset] duration-500 ease-out", over ? "stroke-fail" : "stroke-brand")}
          />
        )}
      </svg>
      <span className={cn("absolute font-semibold leading-none tracking-tight", size >= 38 ? "text-[11px]" : "text-[9px]", over ? "text-fail" : "text-foreground")}>
        {pct}
      </span>
    </span>
  );
}

/** Plan name + monthly step-usage: a compact footer strip. The ring shows the
 * percentage of quota used; the exact count lives on the line beside it.
 * Collapses to just the ring (with a tooltip) in icon mode. */
export function PlanUsage() {
  const plan = useSession((s) => s.plan) ?? "free";
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  const q = useQuery({ queryKey: ["usage"], queryFn: () => usageApi.get(), refetchInterval: 60_000 });
  const u = q.data;

  const unlimited = !u || u.stepsIncluded == null;
  const used = u?.stepsUsed ?? 0;
  const included = u?.stepsIncluded ?? 0;
  const frac = unlimited || !included ? 0 : used / included;
  const over = u?.status === "over";
  const planLabel = PLAN_LABEL[plan] ?? plan;
  const tip = `${planLabel} · ${used.toLocaleString()}${unlimited ? "" : ` / ${included.toLocaleString()}`} executions this month`;

  if (collapsed) {
    return (
      <div className="flex justify-center py-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <span aria-label={tip}>
              <Ring frac={frac} over={over} unlimited={unlimited} size={32} stroke={3} />
            </span>
          </TooltipTrigger>
          <TooltipContent side="right">{tip}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="mx-2 flex items-center gap-3 rounded-md border bg-background/40 px-2.5 py-2">
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12px] font-semibold leading-tight">
          {planLabel} <span className="font-normal text-muted-foreground">plan</span>
        </div>
        <div className="mt-1 truncate font-mono text-[10.5px] leading-tight text-muted-foreground">
          {unlimited ? `${compact(used)} executions this month` : `${compact(used)} of ${compact(included)} executions`}
        </div>
      </div>
      <Ring frac={frac} over={over} unlimited={unlimited} size={40} stroke={3.5} />
    </div>
  );
}
